import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const EMPTY = { organizationName: "", addressLine1: "", city: "", state: "", zip: "", phone: "" };

const field = {
  padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12,
  fontFamily: "inherit", width: "100%", boxSizing: "border-box",
};
const label = { fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 3 };

/**
 * Admin: the extra clinics or hospitals a directory provider works at. They appear, with the main
 * listing, on that provider's search card. Styled like the rest of the provider admin page
 * (plain inputs and buttons) so it sits naturally inside the edit panel.
 */
export default function ProviderOrganizationsEditor({ npi, primaryName }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/v1/admin/providers/${npi}/affiliations`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 403
        ? "Only an admin can manage organizations."
        : err.response?.data?.error || "Could not load organizations.");
    }
  }, [npi]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const add = async () => {
    if (!form.organizationName.trim()) {
      setError("Organization name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/admin/providers/${npi}/affiliations`, form);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(err.response?.status === 403
        ? "Only an admin can manage organizations."
        : err.response?.data?.error || "Could not add that organization.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.organizationName}" from this provider?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/admin/providers/${npi}/affiliations/${a.id}`);
      await load();
    } catch (err) {
      setError(err.response?.status === 403
        ? "Only an admin can manage organizations."
        : err.response?.data?.error || "Could not remove that organization.");
    } finally {
      setBusy(false);
    }
  };

  const affiliations = data?.affiliations || [];

  return (
    <div style={{ marginBottom: 16, padding: 14, background: "white", border: "1px solid #e5e7eb", borderRadius: 10 }}>
      <label style={{ ...label, fontSize: 11, color: "#0B2545" }}>Organizations: where this provider is employed or affiliated</label>
      <p style={{ fontSize: 11, color: "#6b7280", margin: "2px 0 8px" }}>
        Shown to patients on this provider's search card. This does not control which patients they can see;
        that is set by their <strong>practices</strong> under Manage Practices &amp; Providers.
      </p>

      <p style={{ fontSize: 12, color: "#374151", margin: "4px 0 8px" }}>
        <strong>Main organization:</strong> {data?.primaryName || primaryName || "none"}
        <span style={{ color: "#9ca3af" }}> (from the directory)</span>
      </p>

      {error && (
        <p style={{ fontSize: 12, color: "#dc2626", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 8px", margin: "0 0 8px" }}>
          {error}
        </p>
      )}

      {affiliations.length === 0 ? (
        <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 10px" }}>No additional organizations.</p>
      ) : (
        affiliations.map((a) => (
          <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f3f4f6" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#0D2B45" }}>{a.organizationName}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                {[a.addressLine1, a.city, a.state, a.zip].filter(Boolean).join(", ")}
                {a.phone ? ` · ${a.phone}` : ""}
              </div>
            </div>
            <button type="button" disabled={busy} onClick={() => remove(a)}
              style={{ padding: "4px 8px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              ✕
            </button>
          </div>
        ))
      )}

      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, marginTop: 6 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div><label style={label}>Organization *</label><input style={field} value={form.organizationName} onChange={set("organizationName")} maxLength={255} /></div>
          <div><label style={label}>Address</label><input style={field} value={form.addressLine1} onChange={set("addressLine1")} maxLength={255} /></div>
          <div><label style={label}>City</label><input style={field} value={form.city} onChange={set("city")} maxLength={100} /></div>
          <div><label style={label}>State</label><input style={field} value={form.state} onChange={set("state")} maxLength={50} /></div>
          <div><label style={label}>ZIP</label><input style={field} value={form.zip} onChange={set("zip")} maxLength={20} /></div>
          <div><label style={label}>Phone</label><input style={field} value={form.phone} onChange={set("phone")} maxLength={50} /></div>
        </div>
        <button type="button" disabled={busy} onClick={add}
          style={{ padding: "7px 14px", background: "#eef2ff", color: "#1A5EA8", border: "1px solid #c7d2fe", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {busy ? "Saving…" : "+ Add organization"}
        </button>
      </div>
    </div>
  );
}
